import { type FC, useState, type MouseEventHandler } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { ModalContainer } from 'dibs-elements/src/ModalContainer';
import { Button } from 'dibs-elements/src/Button';
import { Link } from 'dibs-elements/exports/Link';
import Close from 'dibs-icons/exports/legacy/Close';
import { PhotosSearchBrowseFacetFilter } from './PhotosSearchBrowseFacetFilter';
import { FACET_TYPES } from './constants';

import styles from './PhotosSearchBrowseFacetFilterModal.scss';

import { type PhotosSearchBrowseFacetFilterModal_photos$data } from './__generated__/PhotosSearchBrowseFacetFilterModal_photos.graphql';
import { type PhotosSearchBrowseFacetFilterModal_viewer$data } from './__generated__/PhotosSearchBrowseFacetFilterModal_viewer.graphql';
import { type FacetSearchPayload, type ModifyFacetHandler } from './types';

type Props = {
    photos: PhotosSearchBrowseFacetFilterModal_photos$data | null | undefined;
    viewer: PhotosSearchBrowseFacetFilterModal_viewer$data | null | undefined;
    facetSearch: FacetSearchPayload;
    onFacetChange: ModifyFacetHandler;
    onFacetDelete: ModifyFacetHandler;
    onSearch: ModifyFacetHandler;
    onFacetsClear: MouseEventHandler<HTMLAnchorElement>;
};

const PhotosSearchBrowseFacetFilterModalComponent: FC<Props> = ({
    photos,
    viewer,
    facetSearch,
    onFacetChange,
    onFacetDelete,
    onFacetsClear,
    onSearch,
}) => {
    const [isRefineModalOpen, setRefineModalOpen] = useState(false);
    const totalResults = photos?.totalResults || 0;
    let appliedFiltersCount = 0;

    Object.keys(facetSearch).forEach(key => {
        const value = facetSearch[key as keyof FacetSearchPayload];
        if (key === FACET_TYPES.SEARCH && value) {
            appliedFiltersCount += 1;
        } else if (value instanceof Array) {
            appliedFiltersCount += value.length;
        }
    });

    function onClose(): void {
        setRefineModalOpen(false);
    }

    return (
        <>
            <Link
                className={styles.refineButton}
                onClick={() => setRefineModalOpen(true)}
                dataTn="refine-button"
            >
                <FormattedMessage
                    id="abt.PhotosSearchBrowseFacetFilterModal.refineButton"
                    defaultMessage="Refine"
                />{' '}
                {appliedFiltersCount > 0 && (
                    <span>
                        (<FormattedNumber value={appliedFiltersCount} />)
                    </span>
                )}
            </Link>

            {isRefineModalOpen && (
                <ModalContainer isOpen={isRefineModalOpen} onClose={onClose} modalSize="fullscreen">
                    <div className={styles.container}>
                        <div className={styles.content}>
                            <div className={styles.header}>
                                <div className={styles.headerLabel}>
                                    <FormattedMessage
                                        id="abt.PhotosSearchBrowseFacetFilterModal.refine"
                                        defaultMessage="Refine"
                                    />
                                </div>
                                <Link className={styles.closeWrapper} onClick={onClose}>
                                    <Close className={styles.closeButton} />
                                </Link>
                            </div>
                            <PhotosSearchBrowseFacetFilter
                                isModalContent
                                photos={photos}
                                viewer={viewer}
                                facetSearch={facetSearch}
                                onFacetChange={onFacetChange}
                                onFacetDelete={onFacetDelete}
                                onFacetsClear={onFacetsClear}
                                onSearch={onSearch}
                            />
                        </div>
                        <div className={styles.footer}>
                            <div className={styles.resultsNumber}>
                                <div>
                                    <FormattedMessage
                                        id="abt.PhotosSearchBrowseFacetFilterModal.numOfResults"
                                        defaultMessage="{totalResults, plural, one {# result found} other {# results found}}"
                                        values={{ totalResults }}
                                    />
                                </div>
                            </div>
                            <Button
                                className={styles.viewResults}
                                onClick={onClose}
                                dataTn="abt-facet-filter-view-results"
                            >
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetFilterModal.viewResults"
                                    defaultMessage="View Results"
                                />
                            </Button>
                        </div>
                    </div>
                </ModalContainer>
            )}
        </>
    );
};

export const PhotosSearchBrowseFacetFilterModal = createFragmentContainer(
    PhotosSearchBrowseFacetFilterModalComponent,
    {
        viewer: graphql`
            fragment PhotosSearchBrowseFacetFilterModal_viewer on Viewer {
                ...PhotosSearchBrowseFacetFilter_viewer
            }
        `,
        photos: graphql`
            fragment PhotosSearchBrowseFacetFilterModal_photos on PhotosSearchBrowseQueryConnection {
                ...PhotosSearchBrowseFacetFilter_photos
                totalResults
            }
        `,
    }
);
